import inspect from 'object-inspect'

import { has, get, constant, truncate } from 'lodash-es'

import supportsProxy from './supports-proxy'

export default supportsProxy() ? LockableObj : FakeLockableObj

const LOCK_READ = 'lockRead'
const LOCK_WRITE = 'lockWrite'

const UNLOCK_READ = 'unlockRead'
const UNLOCK_WRITE = 'unlockWrite'

function LockableObj(obj) {
  let readLocked = false
  let writeLocked = false

  const extra = [
    LOCK_READ,
    LOCK_WRITE,
    UNLOCK_READ,
    UNLOCK_WRITE
  ]

  const proxy = new Proxy(obj, {
    // read
    get: function lockableObj_get(target, property) {
      if (property === LOCK_READ) return lockRead
      if (property === LOCK_WRITE) return lockWrite
      if (property === UNLOCK_READ) return unlockRead
      if (property === UNLOCK_WRITE) return unlockWrite

      const error = readLocked && has(target, property)
      if (error) {
        throw new Error(`LockableObj#get: ${inspect(property)} cannot be read from ${className(obj)} ${inspectShort(obj)} because object is locked!`)
      }

      return target[property]
    },
    has: function lockableObj_has(target, property) {
      return readLocked ? false : property in target
    },
    ownKeys: function lockableObj_ownKeys(target) {
      return readLocked ? [] : Object.keys(target)
    },
    getOwnPropertyDescriptor: function lockableObj_descriptor(target, property) {
      return readLocked ? undefined : Object.getOwnPropertyDescriptor(target, property)
    },
    // write
    set: function lockableObj_set(target, property, value) {
      const restricted = extra.indexOf(property) !== -1
      if (restricted) {
        throw new Error(`LockableObj#set: ${inspect(property)} cannot be written to ${className(obj)} ${inspectShort(obj)} because it's restricted!`)
      }

      if (writeLocked) {
        throw new Error(`LockableObj#set: ${inspect(property)} cannot be written to ${className(obj)} ${inspectShort(obj)} because object is locked!`)
      }

      target[property] = value
      return true
    },
    deleteProperty: function lockableObj_delete(target, property) {
      const restricted = extra.indexOf(property) !== -1
      if (restricted) {
        throw new Error(`LockableObj#deleteProperty: ${inspect(property)} cannot be deleted from ${className(obj)} ${inspectShort(obj)} because it's restricted!`)
      }

      if (writeLocked) {
        throw new Error(`LockableObj#deleteProperty: ${inspect(property)} cannot be deleted from ${className(obj)} ${inspectShort(obj)} because object is locked!`)
      }

      return delete target.property
    },
    defineProperty: function lockableObj_define(target, property, propertyDescriptor) {
      const restricted = extra.indexOf(property) !== -1
      if (restricted) {
        throw new Error(`LockableObj#defineProperty: ${inspect(property)} cannot be defined in ${className(obj)} ${inspectShort(obj)} because it's restricted!`)
      }

      if (writeLocked) {
        throw new Error(`LockableObj#defineProperty: ${inspect(property)} cannot be defined in ${className(obj)} ${inspectShort(obj)} because object is locked!`)
      }

      return Object.defineProperty(target, property, propertyDescriptor)
    }
  })

  return proxy

  function lockRead() {
    readLocked = true
    return proxy
  }

  function lockWrite() {
    writeLocked = true
    return proxy
  }

  function unlockRead() {
    readLocked = false
    return proxy
  }

  function unlockWrite() {
    writeLocked = false
    return proxy
  }
}

function FakeLockableObj(obj) {
  obj[LOCK_READ] = constant(obj)
  obj[LOCK_WRITE] = constant(obj)

  obj[UNLOCK_READ] = constant(obj)
  obj[UNLOCK_WRITE] = constant(obj)

  return obj
}

function inspectShort(v) {
  return truncate(inspect(v, { depth: 1 }), { length: 300 })
}

function className(v) {
  try {
    return get(v, 'constructor.name', Object.prototype.toString.call(v))
  } catch (_err) {
    return `typeof=${typeof v}`
  }
}
